<template>
    <div id="pwa-intervention-plan" class="mx-2">
        <div class="flex items-center justify-between">
            <button-go-back text="Détails intervention"></button-go-back>
            <div class="mb-2.5">
                <div class="flex items-center space-x-2">
                    <md-button class="md-icon-button md-default md-raised my-0" @click="selectedDevice = null">
                        <font-awesome-icon class="md-icon" :icon="['fa', 'times-circle']" />
                    </md-button>
	                <md-button class="md-icon-button md-info md-raised my-0" @click="panel.placed = true">
		                <font-awesome-icon class="md-icon" :icon="['fa', 'map-marked-alt']" />
	                </md-button>
                    <md-button class="md-icon-button md-info md-raised my-0" @click="panel.available = true">
                        <font-awesome-icon class="md-icon" :icon="['fa', 'layer-group']" />
                    </md-button>
                </div>
            </div>
        </div>

        <div class="md-layout md-gutter">
            <div class="md-layout-item md-size-100">
                <div class="intervention-details__card relative">
                    <p class="font-medium mb-2 text-xs">{{ plan.name }}</p>
                    <md-divider class="mb-1.5 -mt-1 -mx-2"></md-divider>
                    <plan-view v-if="!loading" :plan="plan" isMobile :selectedDevice="selectedDevice" @clicked="handlePositions"></plan-view>
                </div>
            </div>
        </div>

        <md-drawer class="md-right" md-fixed :md-active.sync="panel.placed">
            <div class="pl-2 mt-12">
	            <div class="flex items-center justify-between mr-1">
		            <p class="font-medium text-sm text-info">Liste des dispositifs placés sur le plan</p>
		            <md-button class="md-icon-button md-default md-small-icon md-rounded-icon md-raised my-0" @click="panel.placed = false">
			            <font-awesome-icon class="md-icon" :icon="['fa', 'times-circle']" />
		            </md-button>
	            </div>
	            <md-divider class="mt-2 mb-2 -mx-2"></md-divider>
	            <site-plan-drawer-container v-if="panel.placed"
	                                        placed
	                                        text-empty="Aucun dispositif placé sur le plan."
	                                        :devices="plan.devices"
	                                        :plan-id="plan.id"
	                                        :selected-device="selectedDevice"
	                                        @selectDevice="selectDevice"
	                                        @removeDevice="removeDeviceFromPlan"></site-plan-drawer-container>
	            <md-divider class="mt-2 mb-0 -mx-2"></md-divider>
            </div>
        </md-drawer>

	    <md-drawer class="md-right" md-fixed :md-active.sync="panel.available">
		    <div class="pl-2 mt-12">
			    <div class="flex items-center justify-between -mb-2 mr-1">
				    <p class="font-medium text-sm text-info">Liste des dispositifs disponibles</p>
				    <md-button class="md-icon-button md-default md-small-icon md-rounded-icon md-raised my-0" @click="panel.available = false">
					    <font-awesome-icon class="md-icon" :icon="['fa', 'times-circle']" />
				    </md-button>
			    </div>
			    <span class="italic text-xs">(P : sur un autre plan)</span>
			    <md-divider class="mt-2 mb-2 -mx-2"></md-divider>
			    <site-plan-drawer-container v-if="panel.available"
			                                text-empty="Aucun dispositif restant."
			                                :devices="remnantsDevices()"
			                                :plan-id="plan.id"
			                                :selected-device="selectedDevice"
			                                @selectDevice="selectDevice"
			                                @removeDevice="removeDeviceFromPlan"></site-plan-drawer-container>
			    <md-divider class="mt-2 mb-0 -mx-2"></md-divider>
		    </div>
	    </md-drawer>

    </div>
</template>

<script>
import ButtonGoBack from "../components/ButtonGoBack";
import PlanView from "@/pages/Sites/Devices/Plans/components/PlanView";
import SitePlanDrawerContainer from "./components/SitePlanDrawerContainer";

import indexedDBMixin from "@/mixins/indexed-db-mixin";

export default {
    name: "PwaInterventionSitePlan",
    components: { ButtonGoBack, PlanView, SitePlanDrawerContainer },
	mixins: [indexedDBMixin],
    data() {
        return {
            plan: {
                name: '',
                devices: []
            },
	        site: {
		        devices: []
	        },
            placements: [
                { value: 1, label: 'Gauche' },
                { value: 2, label: 'Haut' },
                { value: 3, label: 'Droite' },
                { value: 4, label: 'Bas' },
            ],
            selectedDevice: null,
	        panel: {
				placed: false,
		        available: false
	        },
	        loading: true
        }
    },
    methods: {
        async getPlanDetails() {
	        const plan = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/plan/${this.$route.params.planId}`)
	        const site = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/site`)

	        // Get devices placed on plan with their details (data and deviceType)
	        const devices = []
	        await Promise.all(plan.value.devices.map(async (deviceId) => {
				let device = site.value.devices.find(device => device.id === deviceId)

		        const deviceType = await this.fetchDataFromIndexedDB(`pwa/device_types/${device.device_type_id}`)
		        delete deviceType.value.intervention_types
		        delete deviceType.value.quantities

		        devices.push({
			        ...device,
			        plan_num: this.placements.find(placement => placement.value === device.plan_num),
			        current_device_type: deviceType.value
		        })
            })).then(() => {
		        devices.sort(this.sortDevices)
	        })

	        // Get devices on site with deviceType
	        const siteDevices = []
	        await Promise.all(site.value.devices.map(async (device) => {
		        const deviceType = await this.fetchDataFromIndexedDB(`pwa/device_types/${device.device_type_id}`)
		        delete deviceType.value.intervention_types
		        delete deviceType.value.quantities

		        siteDevices.push({
			        ...device,
			        current_device_type: deviceType.value
		        })
	        })).then(() => {
				siteDevices.sort(this.sortDevices)
	        })

            this.plan = { ...plan.value, devices: devices }
	        this.site = { ...site.value, devices: siteDevices }
        },
        async updateDevicePosition(x, y) {
            if(this.selectedDevice) {
                try {
					// Move device on plan
                    this.selectedDevice.plan_x = x
                    this.selectedDevice.plan_y = y

	                const plan = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/plan/${this.plan.id}`)
	                const site = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/site`)

	                // Add selectedDevice in plan data if not exist
	                if(!plan.value.devices.find(deviceId => deviceId === this.selectedDevice.id)) {
		                plan.value.devices.push(this.selectedDevice.id)

		                // Add selectedDevice type in legend in no exist
		                if(!plan.value.deviceTypes[this.selectedDevice.current_device_type.name]) {
			                plan.value.deviceTypes[this.selectedDevice.current_device_type.name] = {
				                id: this.selectedDevice.current_device_type.id,
				                icon: this.selectedDevice.current_device_type.icon,
				                name: this.selectedDevice.current_device_type.name
			                }
		                }
	                }

					let oldPlan
	                const isNotSamePlan = this.selectedDevice.plan_id && (plan.value.id !== this.selectedDevice.plan_id)
					if(isNotSamePlan) {
						oldPlan = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/plan/${this.selectedDevice.plan_id}`)
						const index = oldPlan.value.devices.indexOf(this.selectedDevice.id)
						oldPlan.value.devices.splice(index, 1)

						let deleteInLegend = true
						oldPlan.value.devices.map(deviceId => {
							const detailsDevice = site.value.devices.find(device => device.id === deviceId)
							if(detailsDevice.device_type_id === this.selectedDevice.device_type_id) {
								deleteInLegend = false
							}
						})

						if(deleteInLegend) {
							delete oldPlan.value.deviceTypes[this.selectedDevice.current_device_type.name]
						}
					}

					// Update data of selectedDevice in site data
	                const device = site.value.devices.find(device => device.id === this.selectedDevice.id)
	                device.plan_id = this.plan.id
	                device.plan_x = x
	                device.plan_y = y
	                device.plan_num = this.selectedDevice.plan_num ?? 1
	                device.plan_orient = this.selectedDevice.plan_orient ?? 0

	                // Update IndexedDB
	                const db = await this.getIndexedDBConnection()
	                const tx = db.transaction('data', 'readwrite')
	                const store = tx.objectStore('data')
	                store.put({ key: `pwa/intervention/${this.$route.params.interventionId}/plan/${this.plan.id}`, value: plan.value })
	                store.put({ key: `pwa/intervention/${this.$route.params.interventionId}/site`, value: site.value })

	                if(isNotSamePlan) {
		                store.put({ key: `pwa/intervention/${this.$route.params.interventionId}/plan/${this.selectedDevice.plan_id}`, value: oldPlan.value })
	                }

	                const payload = {
		                access_token: JSON.parse(localStorage.getItem('pwa')).access_token,
		                device_id: this.selectedDevice.id,
		                intervention_id: this.$route.params.interventionId,
		                plan_id: this.plan.id,
		                plan_x: x,
		                plan_y: y,
	                }

	                await this.storeRequest(`pwa/plans/${this.plan.id}/device/${this.selectedDevice.id}`, payload)
	                await this.getPlanDetails()
                } catch (e) {
	                await this.$store.dispatch('alerts/error', 'Une erreur est survenue')
                }
            }
        },
	    async removeDeviceFromPlan(device) {
			try {
				const plan = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/plan/${this.plan.id}`)
				const site = await this.fetchDataFromIndexedDB(`pwa/intervention/${this.$route.params.interventionId}/site`)

				// Update devices list in plan
				const index = plan.value.devices.indexOf(device.id)
				plan.value.devices.splice(index, 1)

				// Update legend in plan
				let deleteInLegend = true
				plan.value.devices.map(deviceId => {
					const detailsDevice = site.value.devices.find(d => d.id === deviceId)
					if(detailsDevice.device_type_id === device.device_type_id) {
						deleteInLegend = false
					}
				})

				if(deleteInLegend) {
					delete plan.value.deviceTypes[device.current_device_type.name]
				}

				// Update device in site
				const deviceOnSite = site.value.devices.find(d => d.id === device.id)
				deviceOnSite.plan_id = null
				deviceOnSite.plan_x = null
				deviceOnSite.plan_y = null
				deviceOnSite.plan_orient = null
				deviceOnSite.plan_num = null

				// Update IndexedDB
				const db = await this.getIndexedDBConnection()
				const tx = db.transaction('data', 'readwrite')
				const store = tx.objectStore('data')
				store.put({ key: `pwa/intervention/${this.$route.params.interventionId}/plan/${this.plan.id}`, value: plan.value })
				store.put({ key: `pwa/intervention/${this.$route.params.interventionId}/site`, value: site.value })

				await this.storeRequest(`pwa/plans/${this.plan.id}/device/${device.id}/remove`, {
					access_token: JSON.parse(localStorage.getItem('pwa')).access_token
				})
				await this.$store.dispatch('alerts/success', 'Dispositif supprimé du plan')
				await this.getPlanDetails()
			} catch (e) {
				await this.$store.dispatch('alerts/error', 'Une erreur est survenue')
			}
	    },
        remnantsDevices() {
            return this.site.devices.filter(device => { return (device.plan_id === null || device.plan_id !== this.plan.id) && device.plan_id !== 0 })
        },
        handlePositions(positions) {
            const { offsetX, offsetY } = positions
            let x = offsetX - 6
            let y = offsetY - 7
            this.updateDevicePosition(x, y)
        },
        selectDevice(e) {
            this.selectedDevice = this.site.devices.find(device => device.id === e.deviceId)
        },
	    sortDevices(a, b) {
		    if (a.number < b.number) {
			    return -1;
		    }
		    if (a.number > b.number) {
			    return 1;
		    }
		    return 0;
	    },
    },
    async mounted() {
        await this.getPlanDetails()
	    this.loading = false
    }
}
</script>

<style scoped>

</style>
